import { ReactNode } from "react"
import { useOverlayScroll } from "./useOverlayScroll"
import { OverlayScrollFooter } from "./OverlayScrollFooter"

interface OverlayScrollBodyProps {
  className?: string;
  readMoreLabel: string;
  // Extra values that should re-run the scrollability check when they change, matching
  // `useOverlayScroll(deps)` (e.g. a body whose height depends on a load/confirm state).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors useOverlayScroll's deps
  deps?: any[];
  children: ReactNode;
}

/**
 * Scrollable overlay body plus its "read more" footer.
 *
 * `useOverlayScroll` has to run unconditionally, in the same order, on every render of
 * whichever component owns it. Calling it directly in a view that early-returns a loading
 * state, or inside an `OverlayView` `render` callback (which runs inline in OverlayView's
 * own body), changes that component's hook list between renders and crashes React with
 * "Rendered more hooks than during the previous render" (#1267). Owning the hook here keeps
 * it bound to a component that renders it exactly once, so callers can mount and unmount
 * the scrollable body freely.
 */
export function OverlayScrollBody({ className, readMoreLabel, deps, children }: OverlayScrollBodyProps) {
  const { bodyRef, showScrollButton, isAtBottom, handleScrollMore } = useOverlayScroll(deps);

  return (
    <>
      <div ref={bodyRef} className={className}>
        {children}
      </div>
      <OverlayScrollFooter isAtBottom={isAtBottom} showScrollButton={showScrollButton} handleScrollMore={handleScrollMore} readMoreLabel={readMoreLabel} />
    </>
  );
}
